import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectAPI, clientAPI, userAPI } from '../services/api';
import useAuthStore from '../store/authStore';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import './ProjectsNew.css';

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [filters, setFilters] = useState({ status: '', category: '', search: '' });
  const [stats, setStats] = useState(null);
  const user = useAuthStore((state) => state.user);

  const projectCategories = [
    'New Software Development',
    'Customization / Enhancement',
    'Bug Fixing & Maintenance',
    'UI/UX Redesign',
    'Integration Work',
    'Testing / QA Projects',
    'R&D / Analysis Work'
  ];

  const projectStatuses = ['Planning', 'Active', 'On Hold', 'Completed', 'Cancelled'];
  const priorities = ['Low', 'Medium', 'High', 'Critical'];

  useEffect(() => {
    loadProjects();
    loadStats();
  }, [filters]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectAPI.getAll(filters);
      const allProjects = response.data.data || [];

      setProjects(allProjects);
      setError(null);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setError(error.response?.data?.message || 'Unable to load projects');
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await projectAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;

    try {
      await projectAPI.delete(projectId);
      toast.success('Project deleted successfully');
      setShowDetailsModal(false);
      setSelectedProject(null);
      loadProjects();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete project');
    }
  };

  const viewProjectDetails = async (project) => {
    try {
      const response = await projectAPI.getById(project._id);
      setSelectedProject(response.data.data);
      setShowDetailsModal(true);
      setActiveTab('overview');
    } catch (error) {
      toast.error('Failed to load project details');
    }
  };



  const getStatusColor = (status) => {
    const colors = {
      Planning: 'planning',
      Active: 'active',
      'On Hold': 'hold',
      Completed: 'completed',
      Cancelled: 'cancelled'
    };
    return colors[status] || 'planning';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      Low: 'low',
      Medium: 'medium',
      High: 'high',
      Critical: 'critical'
    };
    return colors[priority] || 'medium';
  };

  const getHealthColor = (health) => {
    const colors = {
      'On Track': 'success',
      'At Risk': 'warning',
      'Off Track': 'danger'
    };
    return colors[health] || 'success';
  };

  const canCreateProject = ['Project Manager', 'Operations Head'].includes(user?.role);
  const canEditProject = ['Project Manager', 'Operations Head'].includes(user?.role);

  return (
    <Layout>
      <div className="projects-new">
        <div className="projects-header">
          <div className="header-left">
            <h1 className="projects-title">Projects</h1>
            <p className="projects-subtitle">
              {user?.role === 'Project Lead'
                ? 'Manage projects you lead'
                : 'Manage software development projects'}
            </p>
          </div>
          {canCreateProject && (
            <button className="btn-create-project" onClick={() => navigate('/projects/add')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Create Project
            </button>
          )}
        </div>

        <div className="projects-stats">
          <div className="stat-card-new primary">
            <svg className="stat-icon-new" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
            </svg>
            <div className="stat-content-new">
              <div className="stat-value-new">{projects.length}</div>
              <div className="stat-label-new">Total Projects</div>
            </div>
          </div>
          <div className="stat-card-new success">
            <svg className="stat-icon-new" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
            <div className="stat-content-new">
              <div className="stat-value-new">{projects.filter(p => p.status === 'Active').length}</div>
              <div className="stat-label-new">Active</div>
            </div>
          </div>
          <div className="stat-card-new completed">
            <svg className="stat-icon-new" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <path d="M22 4L12 14.01l-3-3" />
            </svg>
            <div className="stat-content-new">
              <div className="stat-value-new">{projects.filter(p => p.status === 'Completed').length}</div>
              <div className="stat-label-new">Completed</div>
            </div>
          </div>
          <div className="stat-card-new warning">
            <svg className="stat-icon-new" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div className="stat-content-new">
              <div className="stat-value-new">{projects.filter(p => p.status === 'On Hold').length}</div>
              <div className="stat-label-new">On Hold</div>
            </div>
          </div>
        </div>

        <div className="projects-filters">
          <div className="filter-search">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="M21 21l-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search projects..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="filter-select-new"
          >
            <option value="">All Status</option>
            {projectStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            className="filter-select-new"
          >
            <option value="">All Categories</option>
            {projectCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <button className="btn-clear-new" onClick={() => setFilters({ status: '', category: '', search: '' })}>
            Clear Filters
          </button>
        </div>

        {loading ? (
          <div className="projects-loading">
            <div className="loading-spinner-new"></div>
            <p>Loading projects...</p>
          </div>
        ) : error ? (
          <div className="projects-empty">
            <p>{error}</p>
            <button className="btn-create-first" onClick={loadProjects}>Retry</button>
          </div>
        ) : (
          <div className="projects-grid-new">
            {projects.length === 0 ? (
              <div className="projects-empty">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                </svg>
                <p>No projects found</p>
                {canCreateProject && (
                  <button className="btn-create-first" onClick={() => navigate('/projects/add')}>
                    Create First Project
                  </button>
                )}
              </div>
            ) : (
              projects.map(project => (
                <div key={project._id} className="project-card-new" onClick={() => viewProjectDetails(project)}>
                  <div className="project-card-header">
                    <h3 className="project-card-title">{project.name}</h3>
                    <div className="project-card-badges">
                      <span className={`project-badge status-${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                      <span className={`project-badge priority-${getPriorityColor(project.priority)}`}>
                        {project.priority}
                      </span>
                    </div>
                  </div>

                  {project.health && (
                    <div className={`project-health health-${getHealthColor(project.health)}`}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                        <path d="M22 4L12 14.01l-3-3" />
                      </svg>
                      {project.health}
                    </div>
                  )}

                  <p className="project-card-category">{project.category}</p>

                  <div className="project-card-info">
                    <div className="info-item-new">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      <span>{project.client?.name || 'No Client'}</span>
                    </div>
                    {project.projectManager && (
                      <div className="info-item-new">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                          <circle cx="8.5" cy="7" r="4" />
                          <path d="M20 8v6M23 11h-6" />
                        </svg>
                        <span>PM: {project.projectManager.name}</span>
                      </div>
                    )}
                    {project.projectLead && (
                      <div className="info-item-new">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <span>Lead: {project.projectLead.name}</span>
                      </div>
                    )}
                    <div className="info-item-new">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 00-3-3.87" />
                        <path d="M16 3.13a4 4 0 010 7.75" />
                      </svg>
                      <span>{project.teamMembers?.length || 0} members</span>
                    </div>
                  </div>

                  {project.progress !== undefined && (
                    <div className="project-progress-new">
                      <div className="progress-bar-new">
                        <div className="progress-fill-new" style={{ width: `${project.progress}%` }}></div>
                      </div>
                      <span className="progress-text-new">{project.progress}%</span>
                    </div>
                  )}

                  <div className="project-card-footer">
                    <div className="project-dates">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span>{project.endDate ? new Date(project.endDate).toLocaleDateString() : 'No deadline'}</span>
                    </div>

                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {showDetailsModal && selectedProject && (
          <div className="modal-overlay-new" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-content-enhanced" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-enhanced">
                <div className="modal-title-section">
                  <div className="modal-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                    </svg>
                  </div>
                  <div>
                    <h2>{selectedProject.name}</h2>
                    <p className="modal-subtitle">{selectedProject.category}</p>
                  </div>
                </div>
                <button className="btn-close-enhanced" onClick={() => setShowDetailsModal(false)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="modal-tabs">
                <button
                  className={`modal-tab ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setActiveTab('overview')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  </svg>
                  Overview
                </button>
                <button
                  className={`modal-tab ${activeTab === 'team' ? 'active' : ''}`}
                  onClick={() => setActiveTab('team')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87" />
                    <path d="M16 3.13a4 4 0 010 7.75" />
                  </svg>
                  Team ({selectedProject.teamMembers?.length || 0})
                </button>
                <button
                  className={`modal-tab ${activeTab === 'details' ? 'active' : ''}`}
                  onClick={() => setActiveTab('details')}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  Details
                </button>
              </div>

              <div className="modal-body-enhanced">
                {activeTab === 'overview' && (
                  <div className="tab-content-overview">
                    <div className="overview-badges">
                      <div className="badge-group">
                        <span className="badge-label">Status</span>
                        <span className={`project-badge status-${getStatusColor(selectedProject.status)}`}>
                          {selectedProject.status}
                        </span>
                      </div>
                      <div className="badge-group">
                        <span className="badge-label">Priority</span>
                        <span className={`project-badge priority-${getPriorityColor(selectedProject.priority)}`}>
                          {selectedProject.priority}
                        </span>
                      </div>
                      {selectedProject.health && (
                        <div className="badge-group">
                          <span className="badge-label">Health</span>
                          <span className={`project-health health-${getHealthColor(selectedProject.health)}`}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                              <path d="M22 4L12 14.01l-3-3" />
                            </svg>
                            {selectedProject.health}
                          </span>
                        </div>
                      )}
                    </div>

                    {selectedProject.progress !== undefined && (
                      <div className="progress-section">
                        <div className="progress-header">
                          <span className="progress-label">Project Progress</span>
                          <span className="progress-percentage">{selectedProject.progress}%</span>
                        </div>
                        <div className="progress-bar-enhanced">
                          <div className="progress-fill-enhanced" style={{ width: `${selectedProject.progress}%` }}></div>
                        </div>
                      </div>
                    )}

                    {selectedProject.description && (
                      <div className="description-section-enhanced">
                        <h3>Description</h3>
                        <p>{selectedProject.description}</p>
                      </div>
                    )}

                    <div className="quick-stats">
                      <div className="quick-stat-card">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        <div>
                          <div className="stat-value">{selectedProject.teamMembers?.length || 0}</div>
                          <div className="stat-label">Team Members</div>
                        </div>
                      </div>
                      <div className="quick-stat-card">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                        <div>
                          <div className="stat-value">{selectedProject.estimatedHours || 0}h</div>
                          <div className="stat-label">Estimated Hours</div>
                        </div>
                      </div>
                      <div className="quick-stat-card">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="12" y1="1" x2="12" y2="23" />
                          <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                        </svg>
                        <div>
                          <div className="stat-value">${selectedProject.budget || 0}</div>
                          <div className="stat-label">Budget</div>
                        </div>
                      </div>
                    </div>

                    <div className="info-grid-enhanced">
                      <div className="info-card-enhanced">
                        <div className="info-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                        <div>
                          <div className="info-label">Client</div>
                          <div className="info-value">{selectedProject.client?.name || 'Not assigned'}</div>
                        </div>
                      </div>
                      <div className="info-card-enhanced">
                        <div className="info-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                            <circle cx="8.5" cy="7" r="4" />
                            <path d="M20 8v6M23 11h-6" />
                          </svg>
                        </div>
                        <div>
                          <div className="info-label">Project Manager</div>
                          <div className="info-value">{selectedProject.projectManager?.name || 'Not assigned'}</div>
                        </div>
                      </div>
                      <div className="info-card-enhanced">
                        <div className="info-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                        </div>
                        <div>
                          <div className="info-label">Project Lead</div>
                          <div className="info-value">{selectedProject.projectLead?.name || 'Not assigned'}</div>
                        </div>
                      </div>
                      <div className="info-card-enhanced">
                        <div className="info-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                          </svg>
                        </div>
                        <div>
                          <div className="info-label">Start Date</div>
                          <div className="info-value">{selectedProject.startDate ? new Date(selectedProject.startDate).toLocaleDateString() : 'Not set'}</div>
                        </div>
                      </div>
                      <div className="info-card-enhanced">
                        <div className="info-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                          </svg>
                        </div>
                        <div>
                          <div className="info-label">End Date</div>
                          <div className="info-value">{selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString() : 'Not set'}</div>
                        </div>
                      </div>
                      <div className="info-card-enhanced">
                        <div className="info-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                          </svg>
                        </div>
                        <div>
                          <div className="info-label">Category</div>
                          <div className="info-value">{selectedProject.category}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'team' && (
                  <div className="tab-content-team">
                    {selectedProject.teamMembers?.length > 0 ? (
                      <div className="team-grid-enhanced">
                        {selectedProject.teamMembers.map((member) => (
                          <div key={member._id} className="team-member-card-enhanced">
                            <div className="member-avatar-enhanced">
                              {member.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="member-info-enhanced">
                              <div className="member-name-enhanced">{member.name}</div>
                              <div className="member-role-enhanced">{member.role}</div>
                              {member.email && <div className="member-email-enhanced">{member.email}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state-enhanced">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 00-3-3.87" />
                          <path d="M16 3.13a4 4 0 010 7.75" />
                        </svg>
                        <p>No team members assigned yet</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'details' && (
                  <div className="tab-content-details">
                    <div className="details-section">
                      <h3>Project Information</h3>
                      <div className="details-list">
                        <div className="detail-row">
                          <span className="detail-label">Project ID</span>
                          <span className="detail-value">{selectedProject._id}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Status</span>
                          <span className={`project-badge status-${getStatusColor(selectedProject.status)}`}>
                            {selectedProject.status}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Priority</span>
                          <span className={`project-badge priority-${getPriorityColor(selectedProject.priority)}`}>
                            {selectedProject.priority}
                          </span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Category</span>
                          <span className="detail-value">{selectedProject.category}</span>
                        </div>
                        {selectedProject.health && (
                          <div className="detail-row">
                            <span className="detail-label">Health Status</span>
                            <span className={`project-health health-${getHealthColor(selectedProject.health)}`}>
                              {selectedProject.health}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="details-section">
                      <h3>Timeline & Budget</h3>
                      <div className="details-list">
                        <div className="detail-row">
                          <span className="detail-label">Start Date</span>
                          <span className="detail-value">{selectedProject.startDate ? new Date(selectedProject.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not set'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">End Date</span>
                          <span className="detail-value">{selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not set'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Budget</span>
                          <span className="detail-value">${selectedProject.budget?.toLocaleString() || 0}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Estimated Hours</span>
                          <span className="detail-value">{selectedProject.estimatedHours || 0} hours</span>
                        </div>
                        {selectedProject.progress !== undefined && (
                          <div className="detail-row">
                            <span className="detail-label">Progress</span>
                            <span className="detail-value">{selectedProject.progress}%</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="details-section">
                      <h3>Team Structure</h3>
                      <div className="details-list">
                        <div className="detail-row">
                          <span className="detail-label">Client</span>
                          <span className="detail-value">{selectedProject.client?.name || 'Not assigned'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Project Manager</span>
                          <span className="detail-value">{selectedProject.projectManager?.name || 'Not assigned'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Project Lead</span>
                          <span className="detail-value">{selectedProject.projectLead?.name || 'Not assigned'}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Team Size</span>
                          <span className="detail-value">{selectedProject.teamMembers?.length || 0} members</span>
                        </div>
                      </div>
                    </div>

                    {selectedProject.gitRepository && (
                      <div className="details-section">
                        <h3>Repository</h3>
                        <div className="details-list">
                          <div className="detail-row">
                            <span className="detail-label">Git Repository</span>
                            <a href={selectedProject.gitRepository} target="_blank" rel="noopener noreferrer" className="detail-link">
                              {selectedProject.gitRepository}
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="modal-footer-enhanced">
                {canEditProject && (
                  <button className="btn-delete-enhanced" onClick={() => handleDeleteProject(selectedProject._id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                    Delete Project
                  </button>
                )}
                <div className="footer-actions">
                  <button className="btn-secondary-enhanced" onClick={() => setShowDetailsModal(false)}>
                    Close
                  </button>
                  {canEditProject && (
                    <button className="btn-primary-enhanced" onClick={() => navigate(`/projects/edit/${selectedProject._id}`)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit Project
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Projects;
